/**
 * buildViewQuote — customer-safe projection of a quote for QuoteDocument.
 *
 * Extracted from QuoteReviewSend.js (2026-08-01). Single implementation of the
 * cost/margin firewall: any value that shouldn't reach a customer PDF is excluded HERE.
 *
 * Consumers (registered in COMPONENT_MAP.md):
 *   - QuoteReviewSend (the send-preview flow)
 *   - SentQuoteView (read-only document on QuoteDetail for sent/lost/expired/archived)
 *
 * @param {object} quote — raw quote record from API
 * @param {object|null} customer — customer record (for lifecycle resolution)
 * @param {object} opts
 * @param {function} opts.getSettingsOptions — from useSettingsContext().getOptions
 * @param {function} opts.getEnumLabel — from useSettingsContext().getEnumLabel
 * @param {boolean} [opts.showNotes=true] — include line-level notes
 * @param {string} [opts.notes=''] — pre-resolved notes string for the document footer
 * @param {string} [opts.terms=''] — pre-resolved T&C string
 * @returns {object|null} viewQuote projection for QuoteDocument
 */
import {
  computeLineItem,
  projectAccessoriesToMaterial,
  resolveQuoteDisplay,
} from './index';
import { separateOptions } from './separateOptions';
import { optionProduct } from './optionDisplayParts';
import { normalizeMarginValue } from './quotePrefill';

// --- local helpers (moved from QuoteReviewSend) ---

// PR-13: consolidated — use hardened normalizer from quotePrefill.
const normalizeMarginPct = normalizeMarginValue;

function resolveCustomerTypeMargin(customer, getSettingsOptions) {
  const typeVal = customer?.customer_type || customer?.customerType;
  if (!typeVal || typeof getSettingsOptions !== 'function') return null;
  const options = getSettingsOptions('customer_types') || [];
  const match = options.find((o) => o?.value === typeVal);
  return normalizeMarginPct(match?.default_margin_pct);
}

function sumDiscounts(discounts, subtotal) {
  if (!Array.isArray(discounts) || discounts.length === 0) return { total: 0, rows: [] };
  let total = 0;
  const rows = discounts.map((d) => {
    const amt = d.type === 'percent'
      ? Math.round(subtotal * (Number(d.value) || 0) / 100)
      : Math.round((Number(d.value) || 0) * 100);
    total += amt;
    return { id: d.id || '', label: d.label || '', amount: amt };
  });
  return { total, rows };
}

// --- main export ---

export default function buildViewQuote(quote, customer, opts = {}) {
  if (!quote) return null;
  const { getSettingsOptions, getEnumLabel, showNotes = true, notes = '', terms = '' } = opts;

  const resolved = resolveQuoteDisplay(quote, customer);

  // Margin engine — same as the builder uses.
  const rawLinesForMath = Array.isArray(resolved.line_items) ? resolved.line_items : [];
  const projected = projectAccessoriesToMaterial(rawLinesForMath);
  const overallMargin = Number(resolved.overall_margin ?? resolved.margin ?? 0);
  // §2 (Iter-20): sent/won/acknowledged quotes must use preferStamp so the
  // derivation reproduces the stored lineTotal. Without this, B0's widened cost
  // (additionalLabor) would produce a different total and trigger false
  // price-override detection on records sent before B0 existed.
  const isSent = !!(quote.signatureSentAt || quote.signature_sent_at ||
    quote.status === 'sent' || quote.status === 'won' || quote.status === 'lost' ||
    quote.status === 'expired' || quote.status === 'archived');
  const marginContext = {
    customerMargin: normalizeMarginPct(customer?.default_margin),
    customerTypeMargin: resolveCustomerTypeMargin(customer, getSettingsOptions),
    quoteMargin: overallMargin,
    preferStamp: isSent,
  };
  const lineTotalById = {};
  for (const li of projected) {
    if (!li || !li.id) continue;
    const c = computeLineItem(li, marginContext);
    lineTotalById[li.id] = c.total || 0;
  }

  // Addresses
  // Defensive: address fields can be nested objects in malformed data (Sample
  // Dealer census finding). Coerce to string to prevent React render crash.
  const safeStr = (v) => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''));
  const billTo = {
    name: safeStr(resolved.customer_name),
    contactName: safeStr(resolved.contact_name || resolved.contact?.name),
    street: safeStr(resolved.billing_address?.street || resolved.billing_address?.street1),
    street2: safeStr(resolved.billing_address?.street2),
    city: safeStr(resolved.billing_address?.city),
    state: safeStr(resolved.billing_address?.state),
    zip: safeStr(resolved.billing_address?.zip),
    phone: safeStr(resolved.contact?.phone),
  };
  const jobAddress = {
    street: safeStr(resolved.service_address?.street || resolved.service_address?.street1),
    street2: safeStr(resolved.service_address?.street2),
    city: safeStr(resolved.service_address?.city),
    state: safeStr(resolved.service_address?.state),
    zip: safeStr(resolved.service_address?.zip),
  };

  // Lines
  const rawLines = Array.isArray(resolved.line_items) ? resolved.line_items : [];
  const nonAccessoryLines = rawLines.filter((li) => li && li.categoryId !== 'accessories');
  const { baseLines, optionLines } = separateOptions(nonAccessoryLines, resolved.quote_options);

  const mapLine = (li) => {
    const derived = lineTotalById[li.id];
    let lineAmount;
    if (typeof derived === 'number' && !Number.isNaN(derived)) {
      lineAmount = derived;
    } else if (li.total != null) {
      lineAmount = Number(li.total);
    } else if (li.lineTotal != null) {
      lineAmount = Number(li.lineTotal);
    } else {
      lineAmount = 0;
    }
    const accessories = Array.isArray(li.accessories) ? li.accessories : [];
    const itemised = accessories
      .filter((a) => a && a.rollUp === false)
      .map((a) => ({
        id: a.id,
        name: a.name || '',
        qty: Number(a.qty) || 0,
        unit: a.unit || '',
        amount: (Number(a.qty) || 0) * (Number(a.unit_cost) || 0),
        areaLabel: li.subAreaLabel || li.areaLabel || '',
      }));
    const itemisedTotal = itemised.reduce((sum, a) => sum + a.amount, 0);
    const displayLineAmount = Math.max(0, lineAmount - itemisedTotal);
    return {
      // IDI-131: `lineAmount` (the parent's full line total) is what an
      // alternate replaces, so its Cost Add/Deduct is measured against it.
      id: li.id,
      name: li.productLabel || li.name || '',
      description: li.salesDescription || li.description || li.lineDescription || '',
      alias: li.alias || '',
      // IDI-131: WHITELIST projection. Stored alternates carry cost, margin,
      // materialCost and laborCost (measured on Q-0089/Q-0075/Q-0086); none of
      // those may reach a customer document, so only the fields below are
      // copied, never a spread of `alt`.
      alternates: Array.isArray(li.alternates) ? li.alternates.map((alt) => {
        const amount = typeof lineTotalById[alt.id] === 'number'
          ? lineTotalById[alt.id]
          : Number(alt.total ?? alt.lineTotal ?? 0);
        return {
          id: alt.id,
          name: alt.productLabel || alt.name || '',
          description: alt.salesDescription || alt.description || '',
          rValue: alt.rValue || '',
          qty: alt.sqft != null ? Number(alt.sqft) : null,
          unit: alt.unit || (alt.sqft != null ? 'sq ft' : ''),
          areaLabel: alt.areaLabel || li.areaLabel || '',
          subAreaLabel: alt.subAreaLabel || li.subAreaLabel || '',
          amount,
          // Signed Cost Add/Deduct vs the parent it would replace (legacy
          // contract: "-$14,819"). Negative = cheaper.
          delta: amount - lineAmount,
        };
      }) : [],
      areaId: li.areaId || null,
      areaLabel: li.areaLabel || '',
      subAreaLabel: li.subAreaLabel || '',
      qty: li.sqft != null ? Number(li.sqft) : null,
      unit: li.unit || (li.sqft != null ? 'sq ft' : ''),
      amount: displayLineAmount,
      notes: showNotes ? (li.notes || '') : '',
      itemisedAccessories: itemised,
      laborAmount: li.laborCost || li.labor_cost || null,
    };
  };

  const lines = baseLines.map(mapLine);
  // IDI-545 (Adrian 10-21) option naming: "<sub-area heading> — <alias>",
  // then the product on its own line, then the note, then the price. Alias and
  // product travel separately; `name` stays the derived single label
  // (alias || product || 'Option') for any consumer that wants one string.
  const options = optionLines.map((li) => {
    const alias = typeof li.alias === 'string' ? li.alias.trim() : '';
    // Same product fallback order as the PDF and the signing page (IDI-545).
    const product = optionProduct(li);
    return {
      id: li.id,
      heading: li.subAreaLabel || li.sub_area_label || '',
      alias,
      product,
      name: alias || product || 'Option',
    notes: showNotes ? (li.notes || '') : '',
    description: li.salesDescription || li.description || li.lineDescription || li.customer_description || '',
    amount: typeof lineTotalById[li.id] === 'number' ? lineTotalById[li.id] : Number(li.total ?? li.lineTotal ?? 0),
    };
  });

  // Totals
  const subtotalFromBaseLines = baseLines.reduce((sum, li) => {
    const derived = lineTotalById[li.id];
    const amt = typeof derived === 'number' ? derived : Number(li.total ?? li.lineTotal ?? 0);
    return sum + (Number.isFinite(amt) ? amt : 0);
  }, 0);
  const subtotal = subtotalFromBaseLines;

  const { total: discountsTotal, rows: discountRows } = sumDiscounts(resolved.discounts, subtotal);
  const discount = discountRows.length > 0
    ? discountsTotal
    : Number(resolved.discount_amount ?? 0);
  const discounts = discountRows.length > 0
    ? discountRows
    : (discount > 0 ? [{ id: 'legacy', label: '', amount: discount }] : []);

  const taxRate = Number(resolved.tax_rate ?? 0);
  const tax = Math.round((subtotal - discount) * taxRate);
  const total = subtotal - discount + tax;

  const enumLabel = typeof getEnumLabel === 'function' ? getEnumLabel : () => '';

  return {
    quoteNumber: resolved.quoteNumber || resolved.quote_number || '',
    date: resolved.created_at ? new Date(resolved.created_at).toLocaleDateString() : new Date().toLocaleDateString(),
    validUntil: resolved.valid_until ? new Date(resolved.valid_until).toLocaleDateString() : '',
    salesperson: (resolved.salespersons?.[0]?.name) || resolved.salesperson_name || '',
    billTo,
    jobAddress,
    sameAsBilling: !!(resolved.same_as_billing ?? true),
    paymentTerms: enumLabel('payment_terms', resolved.payment_term_label || resolved.payment_term, { quoteDefaultsField: 'payment_terms' }) || (resolved.payment_term_label || resolved.payment_term || ''),
    paymentType: enumLabel('payment_types', resolved.payment_type, { quoteDefaultsField: 'payment_type' }),
    lines,
    options,
    subtotal,
    discount,
    discounts,
    tax,
    taxLabel: taxRate ? `Tax (${(taxRate * 100).toFixed(1)}%)` : 'Tax',
    total,
    notes,
    terms,
    attachments: Array.isArray(resolved.attachments) ? resolved.attachments : [],
  };
}
